import {
  getReactProps,
  processMachAnimation,
  convertToInlineStyle,
  AnimationCallbackValue,
  Animation,
  setInlineStyle,
  getSplitArr,
} from '@m2h/common/utils/styleUtils';
import { throttle } from 'lodash';
import MachproHTMLElement from './common/machproHtmlElement';
import {
  installDirectionalStyleAccessors,
  normalizeDirectionalStyleText,
  setNativeStyleProperty,
} from './common/directionalStyle';
import { LayoutStabilityScheduler } from './layoutStabilityScheduler';

const defaultClassName = 'machweb-view';

export default class View extends MachproHTMLElement {
  resizeObserver: undefined | ResizeObserver;
  reactProps: any;
  observered: boolean | undefined;
  isSettingAnimation: boolean | undefined;
  // 添加样式监听相关属性
  private styleObserver: MutationObserver | undefined;
  private layoutScheduler: LayoutStabilityScheduler;
  private initLayoutTimer: ReturnType<typeof setTimeout> | undefined;
  private lifecycleEpoch = 0;

  // 使用 throttle 包装的 setAnimation 方法
  private throttledSetAnimation: (animationOptions: Animation, callback?: (value: AnimationCallbackValue) => void) => void;

  // 添加 observedAttributes 静态方法
  static get observedAttributes() {
    return ['style', 'class', 'className', 'classname'];
  }

  constructor() {
    super();
    installDirectionalStyleAccessors(this.style, (logicalProperty, value) => {
      this.isProcessingStyle = true;
      try {
        setNativeStyleProperty(this.style, logicalProperty, value);
        this.lastStyleValue = this.style.cssText;
      } finally {
        this.isProcessingStyle = false;
      }
    });
    this.setupStyleObserver();
    this.layoutScheduler = new LayoutStabilityScheduler({
      sample: () => {
        const rect = this.getBoundingClientRect();
        return {
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          scrollY: window.scrollY,
        };
      },
      deliver: () => this.deliverStableLayout(),
      isActive: () => this.isConnected,
      requestFrame: (callback) => window.requestAnimationFrame(callback),
      cancelFrame: (id) => window.cancelAnimationFrame(id),
      setWatchdog: (callback, timeout) => setTimeout(callback, timeout),
      clearWatchdog: (id) => clearTimeout(id),
      now: () => performance.now(),
      warn: (message, error) => console.warn(message, error || ''),
    });

    // 在构造函数中创建 throttled 版本的 setAnimation
    // this.throttledSetAnimation = throttle(this._setAnimationCore.bind(this), 2000, {
    this.throttledSetAnimation = throttle(this._setAnimationCore.bind(this), 500, {
      leading: true, // 第一次调用立即执行
      trailing: false // 不在延迟结束后执行
    });
  }

  // 设置样式变化监听器
  private setupStyleObserver() {
    this.styleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const newStyleValue = this.getAttribute('style') || '';
          this._updateStyle(newStyleValue);
        }
      });
    });
  }

  // 执行时机早于connectedCallback
  // 某些浏览器中，直接修改 style 对象也会触发 attributeChangedCallback
  attributeChangedCallback(name, oldValue, newValue) {
    // 值无变化不执行任何动作
    if (oldValue === newValue) return;
    // className属性打包时处理为class，运行时需要在属性变化后同步到webcomponent
    if (name === 'className' || name === 'classname' || name === 'class') {
      this._updataClassName(newValue);
      return;
    }

    if (name === 'style') {
      this._updateStyle(newValue);
      return;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.lifecycleEpoch += 1;
    const currentEpoch = this.lifecycleEpoch;

    if (!this.styleObserver) {
      this.setupStyleObserver();
    }

    // 开始监听样式变化
    if (this.styleObserver) {
      this.styleObserver.observe(this, {
        attributes: true,
        attributeFilter: ['style']
      });
    }

    if (this.getAttribute('mstyle')) {
      // 获取babel转化后的style
      this._updateStyle(`${this.style.cssText}${this.getAttribute('mstyle')}`);
    }

    this.style.setProperty = this.setProperty.bind(this);
    /**
     * 渲染完成事件
     * onLayout 会在 ref 的回调之前执行，导致节点的赋值失效
     * 这里 hack 一下来延迟 onLayout 的执行
     */
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this; // timeout里使用this有点问题可能回到window

    if (this.initLayoutTimer) clearTimeout(this.initLayoutTimer);
    this.initLayoutTimer = setTimeout(() => {
      this.initLayoutTimer = undefined;
      if (currentEpoch !== this.lifecycleEpoch || !self.isConnected) return;
      self.reactProps = getReactProps(self) || {};
      if (self.reactProps) {
        if (self.reactProps.onLayout) {
          try {
            self.handleCycleCb([self.reactProps.onLayout]);
          } finally {
            // A business callback failure must not permanently prevent this
            // lifecycle from registering its ResizeObserver.
            if (currentEpoch === self.lifecycleEpoch && self.isConnected) {
              self.observerDomChange();
            }
          }
        }
        // react某些动态样式转换，在初始化时还没有值
        if (self.reactProps.style) {
          const reactPropsStyleStr = convertToInlineStyle(self.reactProps.style);
          self._updateStyle(`${self.style.cssText}${reactPropsStyleStr}`);
        }
      }
    }, 0);
  }

  // 所有更新this.className的统一调用此处，更新class时，保持默认样式
  _updataClassName(newValue = this.className) {
    if (!this.className && !newValue) {
      this.className = defaultClassName;
      return;
    }

    if (this.className === newValue) return;

    const classArr = getSplitArr(newValue); // 拆为数组来判断，避免有包含相同字符串的情况
    const hasDefault = classArr.includes(defaultClassName);

    if (!hasDefault) {
      classArr.push(defaultClassName); // defaultClassName放最前
    }

    this.className = classArr.join(' ');
  }

  // 所有更新tthis.style.cssText的统一调用此处,新增方法：应用 style 属性
  _updateStyle(styleString, otherProcess?) {
    if (this.isProcessingStyle) return; // 防止重入
    const normalizedStyleString = normalizeDirectionalStyleText(styleString);
    if (this.lastStyleValue === normalizedStyleString) return;
    try {
      this.isProcessingStyle = true;
      this.lastStyleValue = normalizedStyleString; // 记录初始值
      this.style.cssText = normalizedStyleString;
      otherProcess && otherProcess(normalizedStyleString);
    } finally {
      this.isProcessingStyle = false;
    }
  }

  // 核心的 setAnimation 实现
  // 是否带单位都支持：
  // const keyFrames = {
  //   from: { transform: 'translateY(0)' },
  //   to: { transform: `translateY(-${transfromHeight}px)` },
  //   to: { transform: `translateY(-${transfromHeight})` },
  // };
  private _setAnimationCore(animationOptions: Animation, callback?: (value: AnimationCallbackValue) => void) {
    this.isSettingAnimation = true;
    if (!animationOptions) {
      return;
    }

    callback && callback({ state: 'start' });
    const { keyframes, options } = processMachAnimation(animationOptions);
    const animation = this.animate(keyframes, options);

    animation.onfinish = () => {
      callback && callback({ state: 'end' });
      this.isSettingAnimation = false;
    };
  }

  // 公开的 setAnimation 方法，使用 throttled 版本
  setAnimation(animationOptions: Animation, callback?: (value: AnimationCallbackValue) => void) {
    this.throttledSetAnimation(animationOptions, callback);
  }

  // 如果需要立即执行动画（绕过 throttle 限制）
  setAnimationImmediate(animationOptions: Animation, callback?: (value: AnimationCallbackValue) => void) {
    this._setAnimationCore(animationOptions, callback);
  }

  // 取消待执行的 throttled 动画
  cancelPendingAnimation() {
    // @ts-ignore
    this.throttledSetAnimation.cancel();
  }

  setProperty(key: string, value: string | null) {
    setInlineStyle({ element: this, styleName: key, value });
  }

  private deliverStableLayout() {
    if (!this.isConnected) return;
    const reactProps = getReactProps(this) || {};
    this.reactProps = reactProps;
    if (typeof reactProps.onLayout !== 'function') return;
    const detail = this.measureInWindow();
    reactProps.onLayout({ detail });
  }

  observerDomChange() {
    if (this.observered) return;
    // const resizeObserver = new ResizeObserver(() => this.handleCycleCb([this.reactProps.onLayout]));
    const resizeObserver = new ResizeObserver(() => {
      const reactProps = getReactProps(this) || {};
      this.reactProps = reactProps;
      if (typeof reactProps.onLayout !== 'function') return;
      // 等待DOM稳定后再执行onLayout, 有动画的，未稳定时，onLayout回传的尺寸不对，导致计算异常（门店-多规格商品，滚动）
      this.layoutScheduler.notify();
    });
    resizeObserver.observe(this);
    this.resizeObserver = resizeObserver;
    this.observered = true;
  }

  // onLayout={() => this.maskView.setAnimation}, onLayout中自身执行自身的setAnimation时，会触发多次onLayout（3次）
  handleCycleCb(callbacks = [this.reactProps.onLayout]) {
    const self = this; // timeout里使用this有点问题可能回到window
    // console.log(self.isSettingAnimation, 'handleCycleCb');
    // @ts-ignore
    // console.log(self.parentElement.isSettingAnimation, 'parentElement handleCycleCb');
    if (
      self.isSettingAnimation
      // @ts-ignore
      // || self.parentElement?.isSettingAnimation
      // || self.previousElementSibling?.isSettingAnimation || self.nextElementSibling?.isSettingAnimation
    ) return;
    callbacks && callbacks.forEach((fn) => {
      const detail = self.measureInWindow();
      fn({ detail });
    });
  }

  disconnectedCallback() {
    this.lifecycleEpoch += 1;
    if (this.initLayoutTimer) {
      clearTimeout(this.initLayoutTimer);
      this.initLayoutTimer = undefined;
    }
    this.layoutScheduler.stop();
    // 取消待执行的 throttled 动画
    // @ts-ignore
    this.throttledSetAnimation.cancel();

    // 清理监听器
    if (this.styleObserver) {
      this.styleObserver.disconnect();
      this.styleObserver = undefined;
    }

    this.resizeObserver && this.resizeObserver.disconnect();
    this.resizeObserver = undefined;
    this.observered = false;
    this.reactProps = {};
    super.disconnectedCallback();
  }
}
